import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, Sparkles, MapPin, Briefcase, Clock, Loader2,
  ArrowUp, Send, Users, Radar, Bot, User,
  MessageSquare, X, Zap, Target, SlidersHorizontal, Paperclip, FileText,
  Cpu, LineChart, Code, Star, ChevronDown, Bookmark, BookmarkCheck, MoreVertical, Mail,
  ChevronLeft, ArrowUpRight, ChevronRight, AlertCircle, Check, PhoneCall
} from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';
import toast from 'react-hot-toast';
import { sendChatMessage, searchCandidates, scoreCandidates, enrichCandidate, getHistory, saveHistory, deleteHistory, uploadJD, fetchCandidateEmail, fetchCandidatePhone } from './coresignalApi';
import CandidateDetailSidebar from './CandidateDetailSidebar';
import HistorySidebar from './HistorySidebar';
import SearchFilterPanel from './SearchFilterPanel';
import OutreachEmailModal from './OutreachEmailModal';

/** Route external images through our server proxy to avoid hotlink blocking */
const proxyImg = (url) => url ? `/api/coresignal/image-proxy?url=${encodeURIComponent(url)}` : null;

/** "+91 98765-43210" / "098765 43210" / "9876543210" -> "+919876543210"; null if not an Indian mobile
 *  (the Qualifying Agent dials Indian mobiles only, same rule as its invite modal). */
const normalizeIndianMobile = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  return /^[6-9]\d{9}$/.test(digits) ? `+91${digits}` : null;
};

/* ═══════════ CHAT MESSAGES LIST ═══════════ */
const ChatMessages = ({ messages, isLoading, endRef }) => (
  <>
    {messages.map((msg, i) => (
      <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        className={cn('flex gap-2.5', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
        {msg.role !== 'user' && (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center mt-0.5 shadow-sm">
            <img src="/logo.svg" alt="TalentCrux" className="w-4 h-4" />
          </div>
        )}
        <div className={cn('max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line',
          msg.role === 'user'
            ? 'bg-gradient-to-br from-[#007BFF] to-[#0062CC] text-white rounded-tr-md'
            : 'bg-gray-50 border border-gray-100 text-gray-700 rounded-tl-md')}>
          {msg.content}
        </div>
        {msg.role === 'user' && (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#007BFF] flex items-center justify-center mt-0.5">
            <User className="w-3.5 h-3.5 text-white" />
          </div>
        )}
      </motion.div>
    ))}
    {isLoading && (
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
          <img src="/logo.svg" alt="TalentCrux" className="w-4 h-4" />
        </div>
        <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-md">
          {[0, 1, 2].map(j => (
            <motion.div key={j} className="w-1.5 h-1.5 rounded-full bg-[#007BFF]/50"
              animate={{ y: [0, -4, 0] }} transition={{ duration: 0.5, repeat: Infinity, delay: j * 0.12 }} />
          ))}
        </div>
      </div>
    )}
    <div ref={endRef} />
  </>
);

/* ═══════════ SCREENING SELECTOR ═══════════ */
const ScreeningSelector = ({ activeScreenings, selectedScreeningId, onSelectScreening }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const jobsMap = new Map();
  (activeScreenings || []).forEach(s => {
    if (!jobsMap.has(s.jobId)) {
      jobsMap.set(s.jobId, { id: s.jobId, title: s.jobTitle, screenings: [] });
    }
    jobsMap.get(s.jobId).screenings.push(s);
  });
  const jobs = Array.from(jobsMap.values());

  const selectedScreening = (activeScreenings || []).find(s => s._id === selectedScreeningId);

  return (
    <div className="relative flex-shrink-0 ml-1.5" ref={menuRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors border",
          selectedScreening 
            ? "bg-[#007BFF]/10 text-[#007BFF] border-[#007BFF]/20 hover:bg-[#007BFF]/20" 
            : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
        )}
      >
        <Briefcase className="w-3.5 h-3.5" />
        <span className="truncate max-w-[120px]">{selectedScreening ? selectedScreening.name : 'Link Screening'}</span>
        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", isOpen && "rotate-180")} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-0 mb-2 w-72 max-h-[300px] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-100 z-50 p-2"
          >
            <div className="text-xs font-semibold text-gray-400 mb-2 px-2 uppercase tracking-wider">Select a Job & Screening</div>
            {jobs.length === 0 ? (
              <div className="px-2 py-3 text-sm text-gray-500 text-center">No active screenings found.</div>
            ) : (
              <div className="flex flex-col gap-1">
                {selectedScreeningId && (
                  <button 
                    onClick={() => { onSelectScreening(''); setIsOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <X className="w-4 h-4" /> Clear Selection
                  </button>
                )}
                {jobs.map(job => (
                  <div key={job.id} className="flex flex-col">
                    <button
                      onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                      className="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group"
                    >
                      <span className="truncate flex-1">{job.title}</span>
                      <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform", expandedJobId === job.id && "rotate-180")} />
                    </button>
                    <AnimatePresence>
                      {expandedJobId === job.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col gap-0.5 py-1 pl-4 pr-1">
                            {job.screenings.map(s => (
                              <button
                                key={s._id}
                                onClick={() => { onSelectScreening(s._id); setIsOpen(false); }}
                                className={cn(
                                  "w-full text-left px-3 py-2 text-[13px] rounded-lg transition-colors flex items-center justify-between",
                                  selectedScreeningId === s._id ? "bg-[#007BFF] text-white" : "text-gray-600 hover:bg-[#007BFF]/5 hover:text-[#007BFF]"
                                )}
                              >
                                <span className="truncate flex-1">{s.name}</span>
                                {selectedScreeningId === s._id && <BookmarkCheck className="w-4 h-4 flex-shrink-0 opacity-80" />}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════ CHAT INPUT BAR ═══════════ */
const ChatInput = ({ onSend, isLoading, inputRef, centered, onUploadJD, activeScreenings, selectedScreeningId, onSelectScreening }) => {
  const [input, setInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUploadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = () => {
    if (!input.trim() || isLoading || isUploading) return;
    onSend(input.trim());
    setInput('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setShowUploadMenu(false);
    if (!file || !onUploadJD) return;

    setIsUploading(true);
    try {
      await onUploadJD(file);
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ''; // reset
    }
  };

  return (
    <div className={cn('w-full', centered ? 'max-w-2xl' : '')}>
      <div className={cn(
        'relative flex items-center gap-1 bg-white border border-gray-200/80 transition-all',
        centered ? 'rounded-2xl shadow-md px-1 focus-within:border-[#007BFF]/40 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-[#007BFF]/10'
          : 'rounded-xl focus-within:border-[#007BFF]/40 focus-within:ring-1 focus-within:ring-[#007BFF]/10'
      )}>
        {activeScreenings !== undefined && (
          <ScreeningSelector 
            activeScreenings={activeScreenings} 
            selectedScreeningId={selectedScreeningId} 
            onSelectScreening={onSelectScreening} 
          />
        )}
        {centered && activeScreenings === undefined && <Search className="ml-4 w-5 h-5 text-gray-400 flex-shrink-0" />}
        {!centered && activeScreenings === undefined && <Search className="ml-3 w-4 h-4 text-gray-400 flex-shrink-0" />}
        
        <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={centered ? "Describe the candidate you're looking for..." : 'Search candidates...'}
          className={cn('flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none',
            centered ? 'px-3 py-4' : 'px-3 py-2.5')} />

        <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={handleFileChange} />

        <div className="relative" ref={menuRef}>
          <button onClick={() => setShowUploadMenu(!showUploadMenu)} disabled={isLoading || isUploading}
            title="Upload options"
            className="flex-shrink-0 flex items-center justify-center rounded-xl p-2 text-gray-400 hover:text-[#007BFF] hover:bg-blue-50 transition-colors disabled:opacity-50">
            {isUploading ? <Loader2 className="w-5 h-5 animate-spin text-[#007BFF]" /> : <Paperclip className="w-5 h-5" />}
          </button>

          <AnimatePresence>
            {showUploadMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
              >
                <div className="p-1.5">
                  <button
                    onClick={() => {
                      fileInputRef.current?.click();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-[#007BFF]/5 hover:text-[#007BFF] transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#007BFF]" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span>Upload JD</span>
                      <span className="text-[11px] text-gray-400 font-normal">Extract filters from a file</span>
                    </div>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button onClick={handleSend} disabled={!input.trim() || isLoading || isUploading}
          className={cn('flex-shrink-0 flex items-center justify-center rounded-xl transition-all mr-1',
            centered ? 'w-10 h-10' : 'w-8 h-8',
            input.trim() && !isLoading && !isUploading ? 'bg-gradient-to-br from-[#007BFF] to-[#0062CC] text-white shadow-sm hover:shadow-md' : 'bg-gray-100 text-gray-400')}>
          {isLoading ? <Loader2 className={cn('animate-spin', centered ? 'w-4 h-4' : 'w-3.5 h-3.5')} />
            : <ArrowUp className={cn(centered ? 'w-4.5 h-4.5' : 'w-3.5 h-3.5')} />}
        </button>
      </div>
    </div>
  );
};

/* ═══════════ FIT SCORE BADGE WITH TOOLTIP ═══════════ */
const FitScoreBadge = ({ scoreData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  if (!scoreData) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 border border-gray-100">
        <Loader2 className="w-3 h-3 text-gray-300 animate-spin" />
      </span>
    );
  }

  const { fitScore, reasons } = scoreData;
  const color = fitScore >= 85 ? 'emerald' : fitScore >= 70 ? 'blue' : fitScore >= 50 ? 'amber' : 'red';
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
  };
  const starColor = {
    emerald: 'text-emerald-500', blue: 'text-blue-500', amber: 'text-amber-500', red: 'text-red-400',
  };

  return (
    <div className="relative inline-flex" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border cursor-default transition-all', colorMap[color])}>
        <Star className={cn('w-3 h-3', starColor[color])} fill="currentColor" />
        {fitScore}
      </span>
      <AnimatePresence>
        {showTooltip && reasons?.length > 0 && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, y: 4, scale: 0.95, x: '-50%' }}
            animate={{ opacity: 1, y: 0, scale: 1, x: '-50%' }}
            exit={{ opacity: 0, y: 4, scale: 0.95, x: '-50%' }}
            transition={{ duration: 0.15 }}
            className="absolute left-1/2 bottom-full mb-2 z-50 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3 pointer-events-none"
          >
            <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-100">
              <Star className={cn('w-3.5 h-3.5', starColor[color])} fill="currentColor" />
              <span className="text-xs font-bold text-gray-800">Fit Score: {fitScore}/100</span>
            </div>
            <ul className="space-y-1.5">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-gray-600 leading-snug">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-2.5 h-2.5 bg-white border-r border-b border-gray-200 transform rotate-45 -mt-[5px]" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════ STATUS BADGE WITH TOOLTIP ═══════════ */
const cleanReplyMessage = (msg) => {
  if (!msg) return '';
  // Strip everything after common email signature markers
  const cutMarkers = ['---------- Original Message', '--------- Original Message', 'Sent from', 'On ', '-- \n', '---\nRef:'];
  let clean = msg;
  for (const marker of cutMarkers) {
    const idx = clean.indexOf(marker);
    if (idx > 0) clean = clean.substring(0, idx);
  }
  return clean.trim();
};

const StatusBadge = ({ label, colorClass, message }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const cleanMsg = cleanReplyMessage(message);

  return (
    <div className="relative inline-flex" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
      <span className={cn('inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border cursor-default', colorClass)}>
        {label}
      </span>
      <AnimatePresence>
        {showTooltip && cleanMsg && (
          <motion.div
            initial={{ opacity: 0, y: -4, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -4, x: '-50%' }}
            transition={{ duration: 0.12 }}
            className="absolute left-1/2 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 pointer-events-none z-50"
          >
            <div className="absolute left-1/2 -translate-x-1/2 -top-[6px] w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45" />
            <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-100">
              <MessageSquare className="w-3.5 h-3.5 text-[#007BFF]" />
              <span className="text-xs font-bold text-gray-800">Candidate Reply</span>
            </div>
            <p className="text-[12px] text-gray-600 leading-snug whitespace-pre-wrap">{cleanMsg}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════ RESULTS TABLE (agentic_search) ═══════════ */
const ResultsTable = ({ profiles, onSelect, selectedId, loading, selectedIds, onToggleSelect, onToggleAll, fitScores, onAction, activeTab, candidateEmails, outreachLogs, shortlistedIdsSet }) => {
  if (!profiles || profiles.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-16 bg-[#F7F9FC]">
        {activeTab === 'shortlist' ? (
          <>
            <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center mb-4 text-[#007BFF] shadow-sm">
              <Star className="w-7 h-7 fill-current" />
            </div>
            <h4 className="text-base font-bold text-gray-800 mb-1">No Selected Candidates</h4>
            <p className="text-sm text-gray-500 text-center max-w-sm leading-relaxed">
              Select candidates from the Search Results tab and click <strong>"Mark as Selected"</strong> to build your list.
            </p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center mb-4 text-[#007BFF] shadow-sm">
              <Users className="w-7 h-7" />
            </div>
            <h4 className="text-base font-bold text-gray-800 mb-1">No Search Results</h4>
            <p className="text-sm text-gray-500 text-center max-w-sm leading-relaxed">
              Adjust search filters or create a new session to find candidates matching this role.
            </p>
          </>
        )}
      </div>
    );
  }

  const allChecked = profiles.length > 0 && profiles.every(p => selectedIds.has(p.id));
  const someChecked = profiles.some(p => selectedIds.has(p.id));

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#F7F9FC] w-full text-left relative px-4 pt-2">
      {/* Persistent Table Action Bar */}
      <div className={cn(
        "flex items-center justify-between px-6 py-3 border-b flex-shrink-0 w-full transition-colors duration-200",
        selectedIds.size > 0 ? "bg-[#007BFF]/5 border-[#007BFF]/20" : "bg-[#F7F9FC] border-gray-200/60"
      )}>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 ? (
            <span className="px-2.5 py-1 rounded-lg bg-[#007BFF] text-white text-xs font-bold shadow-sm">
              {selectedIds.size} selected
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {activeTab === 'search' ? (
            <button 
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => { if (onAction) onAction('shortlist', Array.from(selectedIds)); }}
              className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap shadow-sm ${
                selectedIds.size > 0 
                  ? 'bg-[#007BFF] text-white hover:bg-[#0062CC] cursor-pointer'
                  : 'bg-[#007BFF]/50 text-white/70 cursor-not-allowed opacity-60'
              }`}
            >
              Mark as Selected
            </button>
          ) : (
            <>
              <button 
                type="button"
                disabled={selectedIds.size === 0}
                onClick={() => { if (onAction) onAction('next-steps', Array.from(selectedIds)); }}
                className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap shadow-sm ${
                  selectedIds.size > 0 
                    ? 'bg-[#007BFF] text-white hover:bg-[#0062CC] cursor-pointer'
                    : 'bg-[#007BFF]/50 text-white/70 cursor-not-allowed opacity-60'
                }`}
              >
                Next Steps
              </button>
              <button 
                type="button"
                disabled={selectedIds.size === 0}
                onClick={() => { if (onAction) onAction('remove-shortlist', Array.from(selectedIds)); }}
                className={`px-4 py-2.5 text-sm font-semibold rounded-lg transition-all border whitespace-nowrap ${
                  selectedIds.size > 0 
                    ? 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 cursor-pointer shadow-sm' 
                    : 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed opacity-60'
                }`}
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto min-h-0 min-w-0 w-full bg-[#F7F9FC] relative">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3">
            <Loader2 className="w-5 h-5 text-[#007BFF] animate-spin" />
            <span className="text-sm text-gray-500">Searching candidates...</span>
          </div>
        ) : (
          <table className="w-full min-w-max text-left border-collapse whitespace-nowrap">
            <thead className="sticky top-0 z-20">
              <tr className="bg-[#007BFF] text-white shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
                <th className="sticky left-0 z-30 bg-[#007BFF] px-4 py-3 w-12 min-w-[48px] text-center shadow-[1px_0_0_#0062CC]">
                  <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => onToggleAll(profiles)} className="w-3.5 h-3.5 rounded border-white/50 bg-white/10 text-white focus:ring-white/30 cursor-pointer transition-colors" />
                </th>
                <th className="sticky left-12 z-30 bg-[#007BFF] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider min-w-[200px]" style={{ boxShadow: '4px 0 8px -4px rgba(0,0,0,0.15)' }}>Candidate</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[120px]">Fit Score</th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider min-w-[100px]">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[160px]">Current Role</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[160px]">Company</th>
                {activeTab !== 'shortlist' && <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[160px]">Location</th>}
                {activeTab !== 'shortlist' && <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[120px]">Industry</th>}
                {activeTab === 'shortlist' && <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[220px]">Email</th>}
                {activeTab === 'shortlist' && <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider min-w-[150px]">Phone</th>}
                <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wider w-14">
                  <ArrowUpRight className="w-3.5 h-3.5 mx-auto" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {profiles.map((p, i) => {
                const isSelected = selectedId === p.id;
                const isChecked = selectedIds.has(p.id);
                const stickyBg = isSelected ? 'bg-[#eff6ff]' : isChecked ? 'bg-blue-50' : 'bg-[#F7F9FC] group-hover:bg-[#eef2f7]';
                return (
                  <motion.tr key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 20) * 0.015 }}
                    onClick={() => onSelect(p)}
                    className={cn('group cursor-pointer transition-all duration-150',
                      isSelected ? 'bg-[#007BFF]/[0.06]' : isChecked ? 'bg-blue-50/50' : 'hover:bg-gray-50/70')}>
                    <td className={cn("sticky left-0 z-10 px-4 py-3.5 text-center min-w-[48px] transition-colors", stickyBg)}>
                      <input type="checkbox" checked={isChecked}
                        onChange={e => { e.stopPropagation(); onToggleSelect(p.id); }}
                        onClick={e => e.stopPropagation()}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]/30 cursor-pointer" />
                    </td>
                    <td className={cn("sticky left-12 z-10 px-4 py-3.5 transition-colors", stickyBg)} style={{ boxShadow: '4px 0 8px -2px rgba(0,0,0,0.04)' }}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#007BFF]/15 to-[#8472B3]/15 flex items-center justify-center text-[10px] font-bold text-[#007BFF] flex-shrink-0">
                          {p.full_name?.charAt(0) || '?'}
                        </div>
                        <span className="text-[13px] font-semibold text-gray-900 truncate max-w-[130px] group-hover:text-[#007BFF] transition-colors">
                          {p.full_name || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 relative hover:z-30">
                      <FitScoreBadge scoreData={fitScores?.[p.id]} />
                    </td>
                    {(() => {
                      if (activeTab === 'search') {
                        const candidateEmail = candidateEmails?.[p.id]?.email?.toLowerCase();
                        const log = outreachLogs?.find(l => 
                          Number(l.candidateId) === Number(p.id) || 
                          (candidateEmail && l.email?.toLowerCase() === candidateEmail)
                        );
                        const isShortlisted = shortlistedIdsSet?.has(Number(p.id));
                        
                        let label, colorClass;
                        if (log) {
                          label = log.status || 'Emailed';
                          colorClass = log.status?.toLowerCase().includes('interested') && !log.status?.toLowerCase().includes('not')
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : log.status?.toLowerCase().includes('not interested')
                            ? 'bg-red-50 text-red-600 border-red-200'
                            : 'bg-blue-50 text-blue-600 border-blue-200';
                        } else if (isShortlisted) {
                          label = 'Selected';
                          colorClass = 'bg-amber-50 text-amber-700 border-amber-200';
                        } else {
                          label = 'Available';
                          colorClass = 'bg-gray-50 text-gray-500 border-gray-200';
                        }
                        return (
                          <td className="px-4 py-2.5 text-center relative hover:z-30">
                            <StatusBadge label={label} colorClass={colorClass} message={log?.message} />
                          </td>
                        );
                      } else {
                        const emailData = candidateEmails?.[p.id];
                        const status = emailData?.status || (p.email ? 'found' : p.emailStatus) || 'pending';
                        const candidateEmail = (emailData?.email || p.email)?.toLowerCase();
                        const log = outreachLogs?.find(l => 
                          Number(l.candidateId) === Number(p.id) || 
                          (candidateEmail && l.email?.toLowerCase() === candidateEmail)
                        );
                        
                        let label = '';
                        let colorClass = '';
                        
                        if (log) {
                          label = log.status || 'Emailed';
                          colorClass = log.status?.toLowerCase().includes('interested') && !log.status?.toLowerCase().includes('not') 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : log.status?.toLowerCase().includes('not interested')
                            ? 'bg-red-50 text-red-600 border-red-200'
                            : 'bg-blue-50 text-blue-600 border-blue-200';
                        } else {
                          const statusMap = {
                            loading: { label: 'Fetching', color: 'bg-blue-50 text-blue-600 border-blue-200' },
                            found: { label: 'Ready', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
                            not_found: { label: 'No Email', color: 'bg-gray-50 text-gray-500 border-gray-200' },
                            pending: { label: 'Pending', color: 'bg-gray-50 text-gray-400 border-gray-200' },
                          };
                          const s = statusMap[status] || statusMap.pending;
                          label = s.label;
                          colorClass = s.color;
                        }
                        
                        return (
                          <td className="px-4 py-2.5 text-center relative hover:z-30">
                            <StatusBadge label={label} colorClass={colorClass} message={log?.message} />
                          </td>
                        );
                      }
                    })()}
                    <td className="px-4 py-2.5"><p className="text-[13px] text-gray-600 truncate max-w-[150px]">{p.active_experience_title || '—'}</p></td>
                    <td className="px-4 py-2.5"><p className="text-[13px] text-gray-600 truncate max-w-[150px]">{p.company_name || '—'}</p></td>
                    {activeTab !== 'shortlist' && <td className="px-4 py-2.5"><p className="text-[13px] text-gray-500 truncate max-w-[150px]">{p.location_full || '—'}</p></td>}
                    {activeTab !== 'shortlist' && (
                      <td className="px-4 py-2.5">
                        {p.company_industry ? (
                          <span className="inline-flex px-2 py-0.5 rounded-md bg-gray-50 text-gray-600 text-xs font-medium truncate max-w-[110px] border border-gray-100">{p.company_industry}</span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                    )}
                    {activeTab === 'shortlist' && (() => {
                      const emailData = candidateEmails?.[p.id];
                      const email = emailData?.email || p.email;
                      const status = emailData?.status || (p.email ? 'found' : p.emailStatus);
                      
                      return (
                        <td className="px-4 py-2.5">
                          {status === 'loading' ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                              <Loader2 className="w-3 h-3 animate-spin" /> Fetching...
                            </span>
                          ) : email ? (
                            <span className="text-[12px] text-gray-700 font-medium truncate max-w-[200px] block">{email}</span>
                          ) : (
                            <span className="text-xs text-gray-300 italic">Not found</span>
                          )}
                        </td>
                      );
                    })()}
                    {activeTab === 'shortlist' && (() => {
                      // A ContactOut sample placeholder ("phone number 1") is not a number
                      const phone = String(p.phone || '').replace(/\D/g, '').length >= 7 ? p.phone : null;
                      return (
                        <td className="px-4 py-2.5">
                          {phone ? (
                            <span className="text-[12px] text-gray-700 font-medium whitespace-nowrap">{phone}</span>
                          ) : p.phoneStatus === 'pending' || candidateEmails?.[p.id]?.status === 'loading' ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400" title="Apollo sends mobile numbers a few minutes after they are requested">
                              <Loader2 className="w-3 h-3 animate-spin" /> Fetching...
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300 italic">Not found</span>
                          )}
                        </td>
                      );
                    })()}
                    <td className="px-4 py-2.5 text-center">
                      {p.linkedin_url ? (
                        <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          className="inline-flex p-1 rounded-md hover:bg-blue-50 text-blue-600 transition-colors"
                          title="View Profile">
                          <ArrowUpRight className="w-4 h-4" />
                        </a>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
};



/* ═══════════ MAIN COMPONENT ═══════════
 *  mode="create"  → compact modal (60vw / 80vh): search bar + filter panel only, no chat
 *  mode="detail"  → full-screen: candidate table LEFT + collapsible chat RIGHT
 *  initialSession → preload an existing session (for "open session" flow)
 * ═══════════════════════════════════════ */
const SourcingAgent = ({ jobId = null, onClose = null, mode = 'create', initialSession = null, jobContext = null }) => {
  const [chatMessages, setChatMessages] = useState(initialSession?.chatMessages || []);
  const [profiles, setProfiles] = useState(initialSession?.profiles || []);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Searching professional databases...');
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichedData, setEnrichedData] = useState(null);
  const [isChatting, setIsChatting] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Tabs & Shortlist state
  const [activeTab, setActiveTab] = useState('search');
  const [shortlist, setShortlist] = useState(() => {
    if (!initialSession) return [];
    const ids = new Set((initialSession.shortlistedIds || []).map(id => Number(id)));
    return (initialSession.profiles || []).filter(p => ids.has(Number(p.id)));
  });
  const shortlistRef = useRef(shortlist);
  useEffect(() => { shortlistRef.current = shortlist; }, [shortlist]);

  const shortlistedIdsSet = useMemo(() => new Set(shortlist.map(p => Number(p.id))), [shortlist]);
  const [candidateEmails, setCandidateEmails] = useState({});
  const [outreachLogs, setOutreachLogs] = useState(initialSession?.outreachLogs || []);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalCandidates, setEmailModalCandidates] = useState([]);

  // Screenings state
  const [activeScreenings, setActiveScreenings] = useState([]);
  const [selectedScreeningId, setSelectedScreeningId] = useState(initialSession?.screeningId || '');
  const selectedScreeningIdRef = useRef(selectedScreeningId);
  useEffect(() => { selectedScreeningIdRef.current = selectedScreeningId; }, [selectedScreeningId]);

  // Next Steps Modal state
  const [showNextStepsModal, setShowNextStepsModal] = useState(false);
  const [nextStepsCandidates, setNextStepsCandidates] = useState([]);
  const [nextStepsChoice, setNextStepsChoice] = useState(null);
  const [nextStepsStage, setNextStepsStage] = useState('choice'); // 'choice' | 'move_destination'
  const [moveDestinationScreeningId, setMoveDestinationScreeningId] = useState('');
  const [movingCandidates, setMovingCandidates] = useState(false);
  // Qualifying Agent step: target screening, phone per candidate id, dialing state
  const [qualifyingScreeningId, setQualifyingScreeningId] = useState('');
  const [callPhones, setCallPhones] = useState({}); // id -> { phone, status: 'loading' | 'found' | 'not_found' | 'error' }
  const [startingCalls, setStartingCalls] = useState(false);
  const phoneLookupRunRef = useRef(0);

  // Filter / search state
  const [filters, setFilters] = useState(initialSession?.filters || null);
  const [showFilterPanel, setShowFilterPanel] = useState(mode !== 'detail'); // false in detail mode → show candidates immediately
  const [noResultsError, setNoResultsError] = useState(false);

  // Session tracking
  const [currentSessionId, setCurrentSessionId] = useState(initialSession?._id || null);
  const currentSessionIdRef = useRef(currentSessionId);
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  const [sourcingName, setSourcingName] = useState(initialSession?.title || jobContext?.title || '');
  const [additionalContext, setAdditionalContext] = useState('');

  const inputRef = useRef(null);
  const endRef = useRef(null);

  // AI Fit Scoring
  const [fitScores, setFitScores] = useState({});
  const scoringAbortRef = useRef(false);

  useEffect(() => {
    const fetchScreenings = async () => {
      try {
        const res = await axios.get('/api/screenings/user/active');
        const screenings = res.data || [];
        const activeJobId = jobId || initialSession?.jobId;
        if (activeJobId) {
          setActiveScreenings(screenings.filter(s => String(s.jobId) === String(activeJobId)));
        } else {
          setActiveScreenings(screenings);
        }
      } catch (err) {
        console.error('Failed to fetch user screenings:', err);
      }
    };
    fetchScreenings();
  }, [jobId, initialSession?.jobId]);

  // Restore scores and emails on initial load if session has profiles
  useEffect(() => {
    if (initialSession?.profiles?.length > 0) {
      const initialScores = {};
      const restoredEmails = {};
      let hasScores = false;
      initialSession.profiles.forEach(p => {
        if (p.fitScore !== undefined && p.fitScore !== null) {
          initialScores[p.id] = { fitScore: p.fitScore, reasons: p.reasons || [] };
          hasScores = true;
        }
        if (p.email) {
          restoredEmails[p.id] = { email: p.email, emailType: p.emailType || 'personal', status: p.emailStatus || 'found' };
        } else if (p.emailStatus === 'not_found') {
          restoredEmails[p.id] = { email: null, emailType: null, status: 'not_found' };
        }
      });
      if (initialSession?.outreachLogs?.length > 0) {
        for (const log of initialSession.outreachLogs) {
          if (log.email && log.candidateId) {
            restoredEmails[log.candidateId] = { email: log.email, emailType: 'outreach', status: 'found' };
          }
        }
      }
      setCandidateEmails(restoredEmails);

      if (hasScores) {
        setFitScores(initialScores);
        // Pre-sort profiles by fit score just in case they aren't sorted
        const sorted = [...initialSession.profiles].sort((a, b) => (b.fitScore || 0) - (a.fitScore || 0));
        setProfiles(sorted);
      } else if (initialSession?.filters) {
        triggerBackgroundScoring(initialSession.profiles, initialSession.filters);
      }
    } else if (initialSession?.outreachLogs?.length > 0) {
      const restoredEmails = {};
      for (const log of initialSession.outreachLogs) {
        if (log.email && log.candidateId) {
          restoredEmails[log.candidateId] = { email: log.email, emailType: 'outreach', status: 'found' };
        }
      }
      setCandidateEmails(restoredEmails);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerBackgroundScoring = useCallback(async (candidateList, searchFilters) => {
    if (!searchFilters?.jobTitle || candidateList.length === 0) return;
    scoringAbortRef.current = false;
    setFitScores({});
    const BATCH_SIZE = 10;
    const tempFitScores = {};
    for (let i = 0; i < candidateList.length; i += BATCH_SIZE) {
      if (scoringAbortRef.current) break;
      const batch = candidateList.slice(i, i + BATCH_SIZE);
      try {
        const scores = await scoreCandidates(batch, searchFilters);
        if (scoringAbortRef.current) break;
        scores.forEach(s => {
          tempFitScores[s.id] = { fitScore: s.fitScore, reasons: s.reasons };
        });
        setFitScores(prev => ({ ...prev, ...tempFitScores }));
      } catch (e) {
        console.error('Background scoring batch failed:', e);
      }
    }
    if (!scoringAbortRef.current) {
      const enrichedProfiles = candidateList.map(p => ({
        ...p,
        fitScore: tempFitScores[p.id]?.fitScore ?? p.fitScore ?? 0,
        reasons: tempFitScores[p.id]?.reasons ?? p.reasons ?? [],
      })).sort((a, b) => b.fitScore - a.fitScore);

      setProfiles(enrichedProfiles);

      if (currentSessionId) {
        saveHistory({
          id: currentSessionId,
          profiles: enrichedProfiles,
          shortlistedIds: shortlistRef.current.map(p => p.id)
        }).catch(e => console.error('[triggerBackgroundScoring] failed to save updated profiles to history:', e));
      }
    }
  }, [currentSessionId]);

  const syncHistory = async (messages, ids, totalFound, sessionId, currentProfiles = null, currentFilters = null, currentScreeningId = null) => {
    try {
      const payload = {
        id: sessionId,
        title: sourcingName || messages.find(m => m.role === 'user')?.content.substring(0, 50) || 'New Search',
        chatMessages: messages,
        candidateIds: ids,
        totalFound,
        shortlistedIds: shortlistRef.current.map(p => p.id),
      };
      if (currentProfiles) payload.profiles = currentProfiles;
      if (currentFilters) payload.filters = currentFilters;
      if (currentScreeningId) payload.screeningId = currentScreeningId;
      if (jobId) payload.jobId = jobId;
      const res = await saveHistory(payload);
      if (!sessionId && res._id) setCurrentSessionId(res._id);
      return res;
    } catch (e) { console.error('[syncHistory] SAVE FAILED:', e?.response?.data || e.message || e); }
  };

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  const handleToggleAll = useCallback((profilesList) => {
    setSelectedIds(prev => {
      const allChecked = profilesList.every(p => prev.has(p.id));
      const n = new Set(prev);
      profilesList.forEach(p => { if (allChecked) n.delete(p.id); else n.add(p.id); });
      return n;
    });
  }, []);

  const fetchEmailsForCandidates = useCallback(async (candidates) => {
    for (const c of candidates) {
      if (!c.linkedin_url && !c.full_name) {
        setCandidateEmails(prev => ({ ...prev, [c.id]: { email: null, emailType: null, status: 'not_found' } }));
        continue;
      }
      setCandidateEmails(prev => ({ ...prev, [c.id]: { email: null, emailType: null, status: 'loading' } }));
      try {
        const activeSessionId = currentSessionIdRef.current;
        const result = await fetchCandidateEmail(c.linkedin_url, c.full_name, c.company_name, activeSessionId, c.id);
        
        // 1. Update candidateEmails lookup state
        setCandidateEmails(prev => ({
          ...prev,
          [c.id]: { email: result.email, emailType: result.emailType, status: result.email ? 'found' : 'not_found' }
        }));

        // 2. Update profiles state reactively (working on the latest state)
        setProfiles(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(p => Number(p.id) === Number(c.id));
          if (idx > -1) {
            updated[idx] = {
              ...updated[idx],
              email: result.email || null,
              emailType: result.emailType || null,
              emailStatus: result.email ? 'found' : 'not_found',
              phone: result.phone || updated[idx].phone || null,
              phoneStatus: result.phone ? 'found' : (result.phoneStatus || updated[idx].phoneStatus || null)
            };
          }
          return updated;
        });

        // 3. Update shortlist state reactively (working on the latest state)
        setShortlist(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(p => Number(p.id) === Number(c.id));
          if (idx > -1) {
            updated[idx] = {
              ...updated[idx],
              email: result.email || null,
              emailType: result.emailType || null,
              emailStatus: result.email ? 'found' : 'not_found',
              phone: result.phone || updated[idx].phone || null,
              phoneStatus: result.phone ? 'found' : (result.phoneStatus || updated[idx].phoneStatus || null)
            };
          }
          return updated;
        });

        if (result.email && selectedScreeningIdRef.current) {
          axios.post(`/api/screenings/${selectedScreeningIdRef.current}/invite`, {
            name: c.full_name || 'Candidate',
            email: result.email,
            status: 'emailed'
          }).catch(e => console.error('Failed to sync candidate to screening', e));
        }
      } catch (err) {
        console.error('Failed to fetch candidate email:', err);
        setCandidateEmails(prev => ({ ...prev, [c.id]: { email: null, emailType: null, status: 'not_found' } }));
      }
    }
  }, []);

  // Write a phone lookup result onto every list that shows the candidate.
  const applyPhoneResult = useCallback((id, result) => {
    const patch = p => (Number(p.id) === Number(id)
      ? {
        ...p,
        phone: result.phone || null,
        phones: result.phones || [],
        phoneStatus: result.phone ? 'found' : (result.phoneStatus || 'not_found'),
        ...(!p.email && result.email ? { email: result.email, emailType: result.emailType } : {}),
      }
      : p);
    setProfiles(prev => prev.map(patch));
    setShortlist(prev => prev.map(patch));
    setNextStepsCandidates(prev => prev.map(patch));
  }, []);

  // Apollo delivers a mobile to our webhook minutes after it is asked for: poll the selected
  // candidates still waiting (Apollo only — no ContactOut spend just for showing the column).
  const pendingPhoneKey = useMemo(
    () => shortlist.filter(p => p.phoneStatus === 'pending').map(p => p.id).join(','),
    [shortlist]
  );
  useEffect(() => {
    if (!pendingPhoneKey) return undefined;
    let stopped = false;
    let busy = false;
    const timer = setInterval(async () => {
      if (busy) return;
      busy = true;
      for (const c of shortlistRef.current.filter(p => p.phoneStatus === 'pending')) {
        if (stopped) break;
        try {
          const result = await fetchCandidatePhone(c.linkedin_url, currentSessionIdRef.current, c.id, false);
          if (!stopped && result.phoneStatus !== 'pending') applyPhoneResult(c.id, result);
        } catch (e) {
          // keep waiting; the server gives up on an unanswered reveal after 30 minutes
        }
      }
      busy = false;
    }, 15000);
    return () => { stopped = true; clearInterval(timer); };
  }, [pendingPhoneKey, applyPhoneResult]);

  // Candidates selected before mobiles were fetched have no phoneStatus: ask Apollo once for each.
  const phoneBackfillRef = useRef(new Set());
  useEffect(() => {
    const todo = shortlist.filter(p =>
      !p.phoneStatus
      && String(p.phone || '').replace(/\D/g, '').length < 7
      && candidateEmails?.[p.id]?.status !== 'loading'
      && !phoneBackfillRef.current.has(p.id));
    if (!todo.length || !currentSessionId) return;
    todo.forEach(p => phoneBackfillRef.current.add(p.id));
    (async () => {
      for (const c of todo) {
        try {
          const result = await fetchCandidatePhone(c.linkedin_url, currentSessionIdRef.current, c.id, false);
          applyPhoneResult(c.id, result);
        } catch (e) {
          // leave it; the Qualifying Agent step looks again
        }
      }
    })();
  }, [shortlist, candidateEmails, applyPhoneResult, currentSessionId]);

  const handleTableAction = useCallback((action, candidateIds) => {
    if (action === 'shortlist') {
      const candidatesToAdd = profiles.filter(p => candidateIds.includes(p.id));
      setShortlist(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newCandidates = candidatesToAdd.filter(c => !existingIds.has(c.id));
        if (newCandidates.length > 0) fetchEmailsForCandidates(newCandidates);
        const updated = [...prev, ...newCandidates];
        if (currentSessionId) {
          saveHistory({ id: currentSessionId, shortlistedIds: updated.map(p => p.id) }).catch(e => console.error('Shortlist sync error', e));
        }
        return updated;
      });
      setSelectedIds(new Set());
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Marked ${candidateIds.length} candidate(s) as selected.` }]);
    } else if (action === 'remove-shortlist') {
      setShortlist(prev => {
        const updated = prev.filter(p => !candidateIds.includes(p.id));
        if (currentSessionId) {
          saveHistory({ id: currentSessionId, shortlistedIds: updated.map(p => p.id) }).catch(e => console.error('Shortlist sync error', e));
        }
        return updated;
      });
      setSelectedIds(new Set());
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Removed ${candidateIds.length} candidate(s) from selected.` }]);
    } else if (action === 'send-email') {
      const selected = shortlist.filter(p => candidateIds.includes(p.id)).map(c => ({
        ...c,
        email: candidateEmails[c.id]?.email || null,
      }));
      setEmailModalCandidates(selected);
      setShowEmailModal(true);
      setSelectedIds(new Set());
    } else if (action === 'next-steps') {
      const selected = shortlist.filter(p => candidateIds.includes(p.id)).map(c => ({
        ...c,
        email: candidateEmails[c.id]?.email || c.email || null,
      }));
      setNextStepsCandidates(selected);
      setNextStepsChoice(null);
      setNextStepsStage('choice');
      setMoveDestinationScreeningId('');
      setQualifyingScreeningId('');
      setShowNextStepsModal(true);
    }
  }, [profiles, shortlist, candidateEmails, fetchEmailsForCandidates, currentSessionId]);

  const handleMoveAndInvite = useCallback(async () => {
    if (!moveDestinationScreeningId) {
      toast.error('Please select a target screening.');
      return;
    }
    
    const candidatesToInvite = nextStepsCandidates.filter(c => c.email);
    if (candidatesToInvite.length === 0) {
      toast.error('No candidates with valid email addresses to invite.');
      return;
    }

    setMovingCandidates(true);
    let successCount = 0;
    let failCount = 0;

    const targetScreening = activeScreenings.find(s => s._id === moveDestinationScreeningId);
    const targetJobTitle = targetScreening?.jobTitle || 'the position';
    const applyLink = `${window.location.origin}/apply/${moveDestinationScreeningId}`;

    try {
      for (const candidate of candidatesToInvite) {
        try {
          // 1. Add/invite candidate to the target screening in the database
          await axios.post(`/api/screenings/${moveDestinationScreeningId}/invite`, {
            name: candidate.full_name || 'Candidate',
            email: candidate.email,
            status: 'emailed'
          });

          // 2. Send the invitation email via emails/send
          await axios.post('/api/emails/send', {
            candidates: [{
              email: candidate.email,
              firstName: candidate.full_name?.split(' ')[0] || 'Candidate',
              lastName: candidate.full_name?.split(' ').slice(1).join(' ') || ''
            }],
            email: {
              subject: `Invitation: Screening for {{jobTitle}} at Indara Technologies`,
              html: `<p>Dear {{First Name}},</p>
<p>We are pleased to invite you to complete a screening for the <strong>{{jobTitle}}</strong> position at Indara Technologies.</p>
<p>This assessment is a key step in our hiring process and should take around 30 minutes to complete.</p>
<p>Please find the screening link below:<br/><a href="{{applyLink}}">{{applyLink}}</a></p>
<p>If you have any questions or need assistance, feel free to reply to this email.</p>
<p>Best regards,<br/>TalentCrux Team</p>`,
              roleName: targetJobTitle,
              companyName: 'Indara Technologies',
              applyLink: applyLink,
            },
            followUp: {
              enabled: false
            }
          });

          successCount++;
        } catch (err) {
          console.error(`Failed to invite candidate ${candidate.full_name}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully invited ${successCount} candidate(s) to "${targetScreening?.name}".`);
      }
      if (failCount > 0) {
        toast.error(`Failed to invite ${failCount} candidate(s).`);
      }

      // Close modal and reset state
      setShowNextStepsModal(false);
      setSelectedIds(new Set());
      
      // Update chat messages to record this action
      setChatMessages(prev => [
        ...prev, 
        { 
          role: 'assistant', 
          content: `Moved and invited ${successCount} candidate(s) to screening "${targetScreening?.name}".` 
        }
      ]);
    } catch (err) {
      console.error('Bulk move and invite error:', err);
      toast.error('An error occurred during move and invite.');
    } finally {
      setMovingCandidates(false);
    }
  }, [moveDestinationScreeningId, nextStepsCandidates, activeScreenings, setSelectedIds, setChatMessages]);

  const qualifyingScreenings = useMemo(
    () => activeScreenings.filter(s => s.includeQualifyingAgent),
    [activeScreenings]
  );

  // Look up phone numbers (ContactOut) for every selected candidate that has none yet.
  const loadPhonesForCall = useCallback(async (candidates) => {
    // A saved placeholder ("phone number 1" from a ContactOut sample key) counts as no phone.
    const hasRealPhone = c => String(c.phone || '').replace(/\D/g, '').length >= 7;
    const initial = {};
    candidates.forEach(c => {
      if (hasRealPhone(c)) initial[c.id] = { phone: c.phone, status: 'found' };
    });
    setCallPhones(initial);
    const runId = ++phoneLookupRunRef.current;

    const missing = candidates.filter(c => !hasRealPhone(c));
    let configError = null;
    const lookup = async (c) => {
      if (configError) {
        setCallPhones(prev => ({ ...prev, [c.id]: { phone: '', status: 'error' } }));
        return;
      }
      setCallPhones(prev => ({ ...prev, [c.id]: { phone: '', status: 'loading' } }));
      try {
        let result = await fetchCandidatePhone(c.linkedin_url, currentSessionIdRef.current, c.id);
        // Apollo's mobile arrives by webhook: wait up to ~3 minutes, then let the recruiter type one
        for (let i = 0; result.phoneStatus === 'pending' && i < 18; i++) {
          if (runId !== phoneLookupRunRef.current) return;
          setCallPhones(prev => ({ ...prev, [c.id]: { phone: '', status: 'pending' } }));
          await new Promise(r => setTimeout(r, 10000));
          if (runId !== phoneLookupRunRef.current) return;
          result = await fetchCandidatePhone(c.linkedin_url, currentSessionIdRef.current, c.id);
        }
        const phone = result.phone || '';
        setCallPhones(prev => ({ ...prev, [c.id]: { phone, status: phone ? 'found' : (result.phoneStatus === 'pending' ? 'slow' : 'not_found') } }));
        applyPhoneResult(c.id, result);
        if (result.email) {
          setCandidateEmails(prev => (prev[c.id]?.email ? prev : { ...prev, [c.id]: { email: result.email, emailType: result.emailType, status: 'found' } }));
        }
      } catch (err) {
        // Account-level problems (no key, sample/no-credit key, rejected key, rate limit) hit
        // every candidate the same way: stop looking and say why once.
        if ([402, 429, 502, 503].includes(err?.response?.status)) {
          configError = err.response.data?.message || 'Phone lookup is unavailable right now.';
        }
        setCallPhones(prev => ({ ...prev, [c.id]: { phone: '', status: 'error' } }));
      }
    };
    // 5 lookups at a time keeps ContactOut under its rate limit
    const queue = [...missing];
    await Promise.all(Array.from({ length: Math.min(5, queue.length) }, async () => {
      while (queue.length) await lookup(queue.shift());
    }));
    if (configError) toast.error(configError);
  }, [applyPhoneResult]);

  // Closing the modal stops the phone waits started for it
  useEffect(() => {
    if (!showNextStepsModal) phoneLookupRunRef.current += 1;
  }, [showNextStepsModal]);

  const handleStartQualifyingCalls = useCallback(async () => {
    if (!qualifyingScreeningId) {
      toast.error('Please select a screening with the Qualifying Agent.');
      return;
    }
    const toCall = nextStepsCandidates
      .map(c => ({ c, phone: normalizeIndianMobile(callPhones[c.id]?.phone) }))
      .filter(x => x.phone);
    if (toCall.length === 0) {
      toast.error('No candidates with a valid Indian mobile number to call.');
      return;
    }

    setStartingCalls(true);
    const target = qualifyingScreenings.find(s => s._id === qualifyingScreeningId);
    try {
      const { data } = await axios.post(`/api/screenings/${qualifyingScreeningId}/qualifying-agent-invite`, {
        candidates: toCall.map(({ c, phone }) => ({
          name: c.full_name || 'Candidate',
          phone,
          email: c.email || undefined,
        })),
      });
      toast.success(data?.message || `Started ${toCall.length} Qualifying Agent call(s).`);
      setShowNextStepsModal(false);
      setSelectedIds(new Set());
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Sent ${toCall.length} candidate(s) to the Qualifying Agent in "${target?.name}". ${data?.message || ''}`.trim() },
      ]);
    } catch (err) {
      console.error('Qualifying Agent invite error:', err);
      toast.error(err?.response?.data?.message || 'Failed to start Qualifying Agent calls.');
    } finally {
      setStartingCalls(false);
    }
  }, [qualifyingScreeningId, nextStepsCandidates, callPhones, qualifyingScreenings, setSelectedIds, setChatMessages]);

  useEffect(() => {
    if (endRef.current && endRef.current.parentElement) {
      endRef.current.parentElement.scrollTo({ top: endRef.current.parentElement.scrollHeight, behavior: 'smooth' });
    }
  }, [chatMessages, isChatting]);

  useEffect(() => { inputRef.current?.focus({ preventScroll: true }); }, []);

  // ═══ Perform search using structured filters ═══
  const performFilterSearch = useCallback(async (searchFilters) => {
    scoringAbortRef.current = true;
    setIsSearching(true);
    setLoadingMessage('Searching professional databases...');
    setProfiles([]);
    setSelectedCandidate(null);
    setShowFilterPanel(false);
    setFilters(searchFilters);
    setFitScores({});
    setNoResultsError(false);

    const userMsg = { role: 'user', content: `Searching for "${searchFilters.jobTitle}"${searchFilters.location ? ` in ${searchFilters.location}` : ''}` };
    const msgsWithUser = [...chatMessages, userMsg];
    setChatMessages(msgsWithUser);

    try {
      const { profiles: results } = await searchCandidates(searchFilters);
      
      if (results.length === 0) {
        setProfiles([]);
        setShowFilterPanel(true);
        setNoResultsError(true);
        toast.error("No candidates found with the applied filters. Please retry with adjusted or more specific filters.");
        const resultMsg = {
          role: 'assistant',
          content: `No candidates found matching your filters for "${searchFilters.jobTitle}"${searchFilters.location ? ` in ${searchFilters.location}` : ''}. Please retry with adjusted or more specific filters.`
        };
        setChatMessages(prev => {
          const next = [...prev, resultMsg];
          syncHistory(next, [], 0, currentSessionId, [], searchFilters, selectedScreeningId);
          return next;
        });
        return;
      }

      setLoadingMessage(`Found ${results.length} candidates. Ranking with AI...`);

      const BATCH_SIZE = 10;
      const tempFitScores = {};

      for (let i = 0; i < results.length; i += BATCH_SIZE) {
        const batch = results.slice(i, i + BATCH_SIZE);
        setLoadingMessage(`Ranking candidates with AI (${i}/${results.length} completed)...`);
        try {
          const scores = await scoreCandidates(batch, searchFilters);
          scores.forEach(s => {
            tempFitScores[s.id] = { fitScore: s.fitScore, reasons: s.reasons };
          });
        } catch (e) {
          console.error('AI candidate scoring failed:', e);
          batch.forEach(c => {
            if (!tempFitScores[c.id]) {
              tempFitScores[c.id] = { fitScore: 0, reasons: ['Failed to compute fit score'] };
            }
          });
        }
      }

      // Attach fit scores to profiles
      const enrichedResults = results.map(p => ({
        ...p,
        fitScore: tempFitScores[p.id]?.fitScore ?? 0,
        reasons: tempFitScores[p.id]?.reasons ?? [],
      }));

      // Sort profiles descending by fitScore
      const sortedResults = enrichedResults.sort((a, b) => b.fitScore - a.fitScore);

      setProfiles(sortedResults);
      setFitScores(tempFitScores);

      const ids = sortedResults.map(p => p.id);
      const resultMsg = {
        role: 'assistant',
        content: `Found ${sortedResults.length} candidates matching your filters for "${searchFilters.jobTitle}"${searchFilters.location ? ` in ${searchFilters.location}` : ''}. Candidates are ranked by AI fit score.`
      };

      setChatMessages(prev => {
        const next = [...prev, resultMsg];
        syncHistory(next, ids, sortedResults.length, currentSessionId, sortedResults, searchFilters, selectedScreeningId).then(() => {
          if (mode === 'create' && onClose) {
            toast.success("Candidates sourced and ranked successfully!");
            setTimeout(() => {
              onClose();
            }, 600);
          }
        });
        return next;
      });

    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Search failed: ${err.response?.data?.message || err.message}` }]);
    } finally {
      setIsSearching(false);
    }
  }, [currentSessionId, selectedScreeningId, chatMessages]);

  const handleSend = useCallback(async (text) => {
    const userMsg = { role: 'user', content: text };
    const newMsgs = [...chatMessages, userMsg];
    setChatMessages(newMsgs);
    setIsChatting(true);
    syncHistory(newMsgs, profiles.map(p => p.id), profiles.length, currentSessionId, profiles, filters, selectedScreeningId);
    try {
      const ai = await sendChatMessage(newMsgs.map(m => ({ role: m.role, content: m.content })));
      if (ai.type === 'filter' && ai.filters) {
        setFilters(ai.filters);
        setShowFilterPanel(true);
        setChatMessages(prev => [...prev, { role: 'assistant', content: ai.message || 'I have prepared the search filters. Please review and adjust them, then click "Search Candidates".' }]);
      } else {
        const newAiMsg = { role: 'assistant', content: ai.message || 'How can I help you find candidates?' };
        setChatMessages(prev => {
          const next = [...prev, newAiMsg];
          syncHistory(next, profiles.map(p => p.id), profiles.length, currentSessionId, profiles, filters, selectedScreeningId);
          return next;
        });
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    } finally { setIsChatting(false); }
  }, [chatMessages, profiles, currentSessionId, filters, selectedScreeningId]);

  const handleUploadJD = useCallback(async (file) => {
    const userMsg = { role: 'user', content: `Uploaded Job Description: ${file.name}` };
    const newMsgs = [...chatMessages, userMsg];
    setChatMessages(newMsgs);
    setIsChatting(true);
    try {
      const response = await uploadJD(file);
      if (response.filters) {
        setFilters(response.filters);
        setShowFilterPanel(true);
        setChatMessages(prev => [...prev, { role: 'assistant', content: response.summary || 'I have extracted search filters from the Job Description.' }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Failed to process the uploaded Job Description. Please try again.' }]);
    } finally { setIsChatting(false); }
  }, [chatMessages]);

  const handleSelectCandidate = useCallback(async (candidate) => {
    setSelectedCandidate(candidate);
    setEnrichedData(null);
    setIsEnriching(true);
    try {
      const enriched = await enrichCandidate(candidate.id);
      setEnrichedData(enriched);
    } catch (e) {
      console.error('Enrichment failed:', e);
    } finally {
      setIsEnriching(false);
    }
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters(null); setShowFilterPanel(true);
  }, []);

  /* ═══════════════════════════════════════
   * CREATE MODE — compact modal 60vw / 80vh
   * ═══════════════════════════════════════ */
  if (mode === 'create') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ duration: 0.25 }}
        className="w-[60vw] h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-[#F7F9FC] to-white">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-base font-bold text-gray-900">Find Candidates</h2>
              <p className="text-[11px] text-gray-400">AI-powered sourcing · Professional profiles</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body: loading / filter panel / input form */}
        {isSearching ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007BFF] to-[#8472B3] flex items-center justify-center">
                <Radar className="w-7 h-7 text-white animate-pulse" />
              </div>
              <motion.div className="absolute inset-0 rounded-2xl"
                animate={{ boxShadow: ['0 0 0 0 rgba(0,123,255,0.3)', '0 0 0 14px rgba(0,123,255,0)'] }}
                transition={{ duration: 1.2, repeat: Infinity }} />
            </div>
            <p className="text-sm font-semibold text-gray-600">{loadingMessage}</p>
          </div>
        ) : isChatting ? (
          <div className="flex-1 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 text-[#007BFF] animate-spin" />
            <p className="text-sm text-gray-500 font-medium">Extracting search filters...</p>
          </div>
        ) : filters ? (
          /* Filter panel after AI extracts filters */
          <div className="flex-1 overflow-hidden">
            <SearchFilterPanel
              filters={filters}
              onSearch={performFilterSearch}
              onReset={handleFilterReset}
              isSearching={isSearching}
              noResultsError={noResultsError}
            />
          </div>
        ) : (
          /* Input form */
          (() => {
            const handleCreateSourcing = async (e) => {
              if (e) e.preventDefault();
              if (!sourcingName.trim()) return;
              setIsChatting(true);
              setFilters(null);

              // Build prompt — use additionalContext if provided, otherwise use the JD/jobContext
              const jobLoc = jobContext?.location;
              const formattedLocation = Array.isArray(jobLoc) ? jobLoc.join(', ') : (jobLoc || '');
              const jobTitle = jobContext?.title || sourcingName;

              if (additionalContext.trim()) {
                // ── Additional context path ──
                // Tell AI the JD defaults, but let the additional context OVERRIDE title/location
                // if the recruiter mentions something different. E.g. "Senior Production Head: Aurangabad"
                // should yield jobTitle="Senior Production Head" and location="Aurangabad", not company.
                const promptLines = [
                  `You are extracting candidate search filters.`,
                  ``,
                  `JD defaults (use as fallback ONLY if additional context does not specify):`,
                  `- Default Job Title: ${jobTitle}`,
                  formattedLocation ? `- Default Location: ${formattedLocation}` : null,
                  ``,
                  `Recruiter's additional requirements (parse this carefully — it may contain a different job title, a city/location, specific skills, experience range, industry, education, or company preferences):`,
                  additionalContext.trim(),
                  ``,
                  `Rules:`,
                  `- If the additional context mentions a job title → use that as jobTitle (override the default).`,
                  `- If the additional context mentions a city/location/place → use that as location (override the default). Do NOT put a city into companyName.`,
                  `- skills: only if explicitly mentioned, else [].`,
                  `- minExpYears / maxExpYears: only if explicitly mentioned, else 0.`,
                  `- industry: only if explicitly mentioned, else "".`,
                  `- companyName: only if a company name is explicitly mentioned, else "". City names are NOT companies.`,
                  `- education: only if explicitly mentioned, else [].`,
                ].filter(l => l !== null).join('\n');

                const newMsgs = [{ role: 'user', content: promptLines }];
                setChatMessages(newMsgs);
                try {
                  const ai = await sendChatMessage(newMsgs.map(m => ({ role: m.role, content: m.content })));
                  if (ai.type === 'filter' && ai.filters) {
                    // Use AI result; fall back to JD values only if AI returned empty
                    const extracted = {
                      ...ai.filters,
                      jobTitle: ai.filters.jobTitle || jobTitle,
                      location: ai.filters.location || formattedLocation,
                    };
                    setFilters(extracted);
                    setShowFilterPanel(true);
                  } else {
                    // Fallback: bare filters with JD title + location
                    setFilters({ jobTitle, location: formattedLocation, skills: [], minExpYears: 0, maxExpYears: 0, industry: '', companyName: '', education: [] });
                    setShowFilterPanel(true);
                  }
                } catch (err) {
                  console.error(err);
                  setFilters({ jobTitle, location: formattedLocation, skills: [], minExpYears: 0, maxExpYears: 0, industry: '', companyName: '', education: [] });
                  setShowFilterPanel(true);
                } finally {
                  setIsChatting(false);
                }
                return;
              }

              // ── No additional context path — use JD/jobContext ──
              let promptText = `Sourcing Name: ${sourcingName}`;
              if (jobContext) {
                const jdParts = [];
                if (jobContext.title) jdParts.push(`Job Title: ${jobContext.title}`);
                if (jobContext.location) jdParts.push(`Location: ${jobContext.location}`);
                if (jobContext.experienceMin != null || jobContext.experienceMax != null) {
                  const min = jobContext.experienceMin ?? 0;
                  const max = jobContext.experienceMax ? `${jobContext.experienceMax}` : '10+';
                  jdParts.push(`Experience: ${min}-${max} years`);
                }
                if (jdParts.length > 0) {
                  promptText += `\nJob Details:\n${jdParts.join('\n')}`;
                }
              }

              const newMsgs = [{ role: 'user', content: promptText }];
              setChatMessages(newMsgs);
              try {
                const ai = await sendChatMessage(newMsgs.map(m => ({ role: m.role, content: m.content })));
                if (ai.type === 'filter' && ai.filters) {
                  setFilters(ai.filters);
                  setShowFilterPanel(true);
                }
              } catch (err) {
                console.error(err);
              } finally {
                setIsChatting(false);
              }
            };

            return (
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <form onSubmit={handleCreateSourcing} className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Sourcing Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={sourcingName}
                      onChange={(e) => setSourcingName(e.target.value)}
                      placeholder="e.g. Node JS Developer Sourcing"
                      className="w-full px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all placeholder:text-gray-400 font-medium text-gray-800"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Additional Context
                    </label>
                    <p className="text-[11px] text-gray-400 mb-1.5">
                      We will automatically include data from the JD of the current job. Add any other specific preferences here.
                    </p>
                    <textarea
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="e.g., worked at product startups, worked on high-scale distributed systems, has B.Tech from tier-1 college..."
                      rows={3}
                      className="w-full px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20 transition-all placeholder:text-gray-400 font-medium text-gray-800 leading-relaxed resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!sourcingName.trim() || isChatting}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#007BFF] to-[#0062CC] hover:from-[#0062CC] hover:to-[#004FA3] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Find Candidates</span>
                  </button>
                </form>
              </div>
            );
          })()
        )}
      </motion.div>
    );
  }

  /* ═══════════════════════════════════════
   * DETAIL MODE — full screen: table + collapsible chat
   * ═══════════════════════════════════════ */
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex min-w-0 w-full h-full bg-[#F7F9FC] overflow-hidden">

      {/* LEFT: Candidate Table */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#F7F9FC]">

        {/* Detail header — screening detail style */}
        <div className="px-5 pt-6 pb-0 bg-[#F7F9FC] flex-shrink-0">
          {/* Top row: back arrow + title + action buttons */}
          <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              {onClose && (
                <button onClick={onClose}
                  className="text-gray-500 hover:text-gray-900 transition-colors flex-shrink-0 -ml-1"
                  title="Back">
                  <ArrowUp className="w-5 h-5 -rotate-90" />
                </button>
              )}
              <h1 className="text-[22px] font-bold text-gray-900 truncate leading-tight">
                {filters?.jobTitle || 'Sourcing Session'}
                {filters?.location
                  ? <span className="text-gray-400 font-semibold"> · {filters.location}</span>
                  : null}
              </h1>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mt-1">
              {filters && (
                <button onClick={() => setShowFilterPanel(v => !v)}
                  className={cn('flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                    showFilterPanel
                      ? 'bg-[#007BFF]/10 text-[#007BFF] border-[#007BFF]/20'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100')}>
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
                </button>
              )}
            </div>
          </div>
          {/* Subtitle */}
          <p className="text-[13px] text-gray-500 mb-3">
            Review and select candidates sourced by AI for this role
          </p>
          {/* Tabs — inline with header background */}
          <div className="flex items-center gap-6 border-b border-gray-200/80 mt-1">
            <button
              onClick={() => { setActiveTab('search'); setSelectedIds(new Set()); }}
              className={cn('pb-4 pt-2 text-[13px] font-medium transition-all relative outline-none flex items-center gap-1.5',
                activeTab === 'search' ? 'text-[#007BFF]' : 'text-gray-500 hover:text-gray-800')}
            >
              <Users className="w-3.5 h-3.5" />
              Search Results
              <span className={cn('px-1.5 py-0.5 rounded-md text-[10px] font-black',
                activeTab === 'search' ? 'bg-[#007BFF]/10 text-[#007BFF]' : 'bg-gray-200/80 text-gray-500')}>
                {profiles.length}
              </span>
              {activeTab === 'search' && (
                <motion.div layoutId="sourcingDetailTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#007BFF] rounded-t-full" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab('shortlist'); setSelectedIds(new Set()); }}
              className={cn('pb-4 pt-2 text-[13px] font-medium transition-all relative outline-none flex items-center gap-1.5',
                activeTab === 'shortlist' ? 'text-[#007BFF]' : 'text-gray-500 hover:text-gray-800')}
            >
              <Star className="w-3.5 h-3.5" />
              Selected
              <span className={cn('px-1.5 py-0.5 rounded-md text-[10px] font-black',
                activeTab === 'shortlist' ? 'bg-[#007BFF]/10 text-[#007BFF]' : 'bg-gray-200/80 text-gray-500')}>
                {shortlist.length}
              </span>
              {activeTab === 'shortlist' && (
                <motion.div layoutId="sourcingDetailTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#007BFF] rounded-t-full" />
              )}
            </button>
          </div>
        </div>

        {/* Filter panel inline */}
        <AnimatePresence>
          {showFilterPanel && filters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden border-b border-gray-100 flex-shrink-0"
            >
              <SearchFilterPanel
                filters={filters}
                onSearch={performFilterSearch}
                onReset={() => setShowFilterPanel(false)}
                isSearching={isSearching}
                noResultsError={noResultsError}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Table / States */}
        {isSearching ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-[#F7F9FC]">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007BFF] to-[#8472B3] flex items-center justify-center">
                <Radar className="w-7 h-7 text-white animate-pulse" />
              </div>
              <motion.div className="absolute inset-0 rounded-2xl"
                animate={{ boxShadow: ['0 0 0 0 rgba(0,123,255,0.3)', '0 0 0 14px rgba(0,123,255,0)'] }}
                transition={{ duration: 1.2, repeat: Infinity }} />
            </div>
            <p className="text-sm font-semibold text-gray-600">{loadingMessage}</p>
          </div>
        ) : (profiles.length > 0 || shortlist.length > 0) ? (
          <ResultsTable
            profiles={activeTab === 'search' ? profiles : shortlist}
            onSelect={handleSelectCandidate}
            selectedId={selectedCandidate?.id}
            loading={isSearching}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleAll={handleToggleAll}
            fitScores={fitScores}
            onAction={handleTableAction}
            activeTab={activeTab}
            candidateEmails={candidateEmails}
            outreachLogs={outreachLogs}
            shortlistedIdsSet={shortlistedIdsSet}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-[#F7F9FC]">
            <div className="text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-500">No candidates yet</p>
              <p className="text-xs text-gray-400 mt-1">Use the filters to refine your search</p>
            </div>
          </div>
        )}
      </div>


      {/* Candidate Detail Sidebar */}
      <CandidateDetailSidebar candidate={selectedCandidate} enrichedData={enrichedData} isEnriching={isEnriching} isOpen={!!selectedCandidate} onClose={() => { setSelectedCandidate(null); setEnrichedData(null); }} searchFilters={filters} />

      {/* Outreach Email Modal */}
      <OutreachEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        candidates={emailModalCandidates}
        jobTitle={filters?.jobTitle || ''}
        company="Indara Technologies"
        sessionId={currentSessionId}
      />

      {/* Next Steps Modal */}
      <AnimatePresence>
        {showNextStepsModal && (() => {
          const linkedScreening = activeScreenings.find(s => s._id === selectedScreeningId);
          const pillLabel = linkedScreening 
            ? `${linkedScreening.jobTitle} - ${linkedScreening.name}`
            : `Sourcing Session`;

          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
              onClick={() => setShowNextStepsModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                className={cn(
                  "bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-100 transition-all duration-300",
                  nextStepsStage === 'choice' ? "w-[95vw] max-w-[920px]" : nextStepsStage === 'qualifying_call' ? "w-[92vw] max-w-[560px]" : "w-[90vw] max-w-[480px]"
                )}
                onClick={e => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="px-6 py-5 border-b border-gray-100 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1.5">
                      <h2 className="text-xl font-bold text-gray-900 leading-tight">Next Steps</h2>
                      <p className="text-xs text-gray-500">
                        Choose what you want to do with these selected candidates.
                      </p>
                      {/* Pill */}
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50/50 text-green-700 border border-green-200 mt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        {pillLabel} · Selected workflow
                      </div>
                    </div>
                    <button onClick={() => setShowNextStepsModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors -mt-1 -mr-1">
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {nextStepsStage === 'choice' ? (
                    <div className="space-y-6">
                      {/* Choose Next Step Sub-Header */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">CHOOSE THE NEXT STEP</div>
                          <p className="text-sm font-semibold text-gray-700">Decide how you want to progress these selected candidates.</p>
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          {nextStepsCandidates.length} candidate{nextStepsCandidates.length !== 1 ? 's' : ''} selected
                        </span>
                      </div>

                      {/* Cards Grid: 3 columns */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        
                        {/* 1. Outreach Candidate */}
                        <div
                          onClick={() => setNextStepsChoice('send_email')}
                          className={cn(
                            "group relative p-5 rounded-xl border flex flex-col justify-between h-[160px] cursor-pointer transition-all duration-200 hover:shadow-md text-left",
                            nextStepsChoice === 'send_email'
                              ? "border-blue-500 bg-blue-50/10 ring-1 ring-blue-500/20"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Mail className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <h4 className="text-[13px] font-bold text-gray-900 leading-tight">Outreach Candidate</h4>
                              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                                Outreach candidate regarding opening in job position and automate the follow-up process.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                            <span className="text-[9px] font-bold text-emerald-600 tracking-wider">CANDIDATE OUTREACH</span>
                            <div className={cn(
                              "w-5 h-5 rounded-full border flex items-center justify-center transition-all",
                              nextStepsChoice === 'send_email'
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-gray-300 bg-white"
                            )}>
                              {nextStepsChoice === 'send_email' && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </div>

                        {/* 2. Move Candidate to Screening */}
                        <div
                          onClick={() => setNextStepsChoice('move_to_screening')}
                          className={cn(
                            "group relative p-5 rounded-xl border flex flex-col justify-between h-[160px] cursor-pointer transition-all duration-200 hover:shadow-md text-left",
                            nextStepsChoice === 'move_to_screening'
                              ? "border-blue-500 bg-blue-50/10 ring-1 ring-blue-500/20"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <ArrowUpRight className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <h4 className="text-[13px] font-bold text-gray-900 leading-tight">Move Candidate to Screening</h4>
                              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                                Move candidate to a screening and send email invitation to start the assessment.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                            <span className="text-[9px] font-bold text-blue-600 tracking-wider">TRANSFER</span>
                            <div className={cn(
                              "w-5 h-5 rounded-full border flex items-center justify-center transition-all",
                              nextStepsChoice === 'move_to_screening'
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-gray-300 bg-white"
                            )}>
                              {nextStepsChoice === 'move_to_screening' && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </div>

                        {/* 3. Call with Qualifying Agent */}
                        <div
                          onClick={() => setNextStepsChoice('qualifying_call')}
                          className={cn(
                            "group relative p-5 rounded-xl border flex flex-col justify-between h-[160px] cursor-pointer transition-all duration-200 hover:shadow-md text-left",
                            nextStepsChoice === 'qualifying_call'
                              ? "border-blue-500 bg-blue-50/10 ring-1 ring-blue-500/20"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <PhoneCall className="w-4.5 h-4.5" />
                            </div>
                            <div>
                              <h4 className="text-[13px] font-bold text-gray-900 leading-tight">Call with Qualifying Agent</h4>
                              <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                                Find their phone numbers and let the AI agent call them to pre-screen interest and fit.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                            <span className="text-[9px] font-bold text-violet-600 tracking-wider">QUALIFYING CALL</span>
                            <div className={cn(
                              "w-5 h-5 rounded-full border flex items-center justify-center transition-all",
                              nextStepsChoice === 'qualifying_call'
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-gray-300 bg-white"
                            )}>
                              {nextStepsChoice === 'qualifying_call' && <Check className="w-3 h-3 stroke-[3]" />}
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Warning if any selected candidate is missing email (email-based steps only) */}
                      {nextStepsChoice !== 'qualifying_call' && nextStepsCandidates.some(c => !c.email) && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 mt-2">
                          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800 leading-normal">
                            Note: <strong>{nextStepsCandidates.filter(c => !c.email).length}</strong> candidate(s) do not have an email address and will be skipped.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : nextStepsStage === 'qualifying_call' ? (
                    <div className="space-y-4">
                      <button
                        onClick={() => setNextStepsStage('choice')}
                        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-2"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back to choices
                      </button>

                      <div className="space-y-3">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Qualifying Agent Screening
                        </label>
                        {qualifyingScreenings.length > 0 ? (
                          <select
                            value={qualifyingScreeningId}
                            onChange={e => setQualifyingScreeningId(e.target.value)}
                            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 bg-white focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/10 outline-none transition-all cursor-pointer"
                          >
                            <option value="">-- Choose a screening --</option>
                            {qualifyingScreenings.map(s => (
                              <option key={s._id} value={s._id}>
                                {s.jobTitle} - {s.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5">
                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800 leading-normal">
                              No screening for this job has the Qualifying Agent set up. Add a Qualifying Agent to a screening first, then come back here.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Candidates to call:</h5>
                        <div className="max-h-56 overflow-y-auto space-y-2 border border-gray-100 rounded-xl p-2 bg-gray-50/50">
                          {nextStepsCandidates.map(c => {
                            const entry = callPhones[c.id] || { phone: '', status: 'loading' };
                            const valid = Boolean(normalizeIndianMobile(entry.phone));
                            return (
                              <div key={c.id} className="flex items-center justify-between gap-3 text-xs px-2 py-1.5 bg-white border border-gray-100 rounded-lg">
                                <span className="font-semibold text-gray-800 truncate max-w-[180px]">{c.full_name}</span>
                                {entry.status === 'loading' || entry.status === 'pending' ? (
                                  <span className="flex items-center gap-1.5 text-gray-400">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    {entry.status === 'pending' ? 'Waiting for Apollo…' : 'Finding number…'}
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <input
                                      value={entry.phone}
                                      onChange={e => {
                                        const phone = e.target.value;
                                        setCallPhones(prev => ({ ...prev, [c.id]: { ...prev[c.id], phone } }));
                                      }}
                                      placeholder={
                                        entry.status === 'error' ? 'Lookup failed — type number'
                                          : entry.status === 'slow' ? 'Apollo still searching — type one'
                                            : 'No number found — type one'
                                      }
                                      className={cn(
                                        "w-[200px] px-2 py-1 border rounded-md text-xs outline-none focus:ring-2 focus:ring-[#007BFF]/10",
                                        entry.phone && !valid ? "border-red-300 text-red-600" : "border-gray-200 text-gray-700"
                                      )}
                                    />
                                    {valid
                                      ? <Check className="w-3.5 h-3.5 text-green-600" />
                                      : <span className="text-red-500 font-medium whitespace-nowrap">Skip</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">
                          Only Indian mobile numbers (+91, starting 6-9) can be called. Candidates without one are skipped.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <button
                        onClick={() => setNextStepsStage('choice')}
                        className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors mb-2"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back to choices
                      </button>

                      <div className="space-y-3">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Select Target Screening
                        </label>
                        <select
                          value={moveDestinationScreeningId}
                          onChange={e => setMoveDestinationScreeningId(e.target.value)}
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 bg-white focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/10 outline-none transition-all cursor-pointer"
                        >
                          <option value="">-- Choose a screening --</option>
                          {activeScreenings.map(s => (
                            <option key={s._id} value={s._id}>
                              {s.jobTitle} - {s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Detailed reminder of candidates who will be invited */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Candidates to invite:</h5>
                        <div className="max-h-36 overflow-y-auto space-y-2 border border-gray-100 rounded-xl p-2 bg-gray-50/50">
                          {nextStepsCandidates.map(c => (
                            <div key={c.id} className="flex items-center justify-between text-xs px-2 py-1 bg-white border border-gray-100 rounded-lg">
                              <span className="font-semibold text-gray-800 truncate max-w-[200px]">{c.full_name}</span>
                              {c.email ? (
                                <span className="text-gray-500 truncate max-w-[200px]">{c.email}</span>
                              ) : (
                                <span className="text-red-500 font-medium">Missing email (will be skipped)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-[#fafbfd]">
                  <button
                    onClick={() => {
                      if (nextStepsStage !== 'choice') {
                        setNextStepsStage('choice');
                      } else {
                        setShowNextStepsModal(false);
                      }
                    }}
                    className="px-4 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg text-sm font-medium transition-all"
                  >
                    Cancel
                  </button>
                  
                  {nextStepsStage === 'choice' ? (
                    <button
                      onClick={() => {
                        if (!nextStepsChoice) {
                          toast.error('Please select an option first.');
                          return;
                        }
                        if (nextStepsChoice === 'send_email') {
                          setShowNextStepsModal(false);
                          setEmailModalCandidates(nextStepsCandidates);
                          setShowEmailModal(true);
                        } else if (nextStepsChoice === 'move_to_screening') {
                          setNextStepsStage('move_destination');
                        } else if (nextStepsChoice === 'qualifying_call') {
                          setNextStepsStage('qualifying_call');
                          if (qualifyingScreenings.length === 1) setQualifyingScreeningId(qualifyingScreenings[0]._id);
                          loadPhonesForCall(nextStepsCandidates);
                        }
                      }}
                      className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all"
                    >
                      Continue
                    </button>
                  ) : nextStepsStage === 'qualifying_call' ? (() => {
                    const callable = nextStepsCandidates.filter(c => normalizeIndianMobile(callPhones[c.id]?.phone)).length;
                    const stillLoading = nextStepsCandidates.some(c => (callPhones[c.id]?.status || 'loading') === 'loading');
                    return (
                      <button
                        onClick={handleStartQualifyingCalls}
                        disabled={!qualifyingScreeningId || callable === 0 || stillLoading || startingCalls}
                        className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        {startingCalls ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Starting calls...
                          </>
                        ) : (
                          <>
                            <PhoneCall className="w-4 h-4" /> Call {callable} candidate{callable !== 1 ? 's' : ''}
                          </>
                        )}
                      </button>
                    );
                  })() : (
                    <button
                      onClick={handleMoveAndInvite}
                      disabled={!moveDestinationScreeningId || movingCandidates}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {movingCandidates ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Moving...
                        </>
                      ) : (
                        'Move & Invite'
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </motion.div>
  );
};

export default SourcingAgent;
